import axios from 'axios';
import { DASHBOARD_API_URL } from '@/lib/constants/login';
import { CIDI_DASHBOARD_API_URL } from '@/lib/constants/dashboard';

/* istanbul ignore next */
export async function axiosInstance(accessToken = '') {
  if(!accessToken) {
    try {
      const store = await import('@/store');
      accessToken = store.default.getters.oktaAccessToken;
    } catch {}
  }
  return axios.create({
    baseURL: DASHBOARD_API_URL,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
}

const _ = { DASHBOARD_API_URL, CIDI_DASHBOARD_API_URL, axios, axiosInstance };

// Get the CI JWT Token
export function getCiTokenCtor(_) {
  return async () => {
    const _axios = _.axiosInstance;
    try {
      const result = await _axios.get('/api/advisor/auth')
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Check and apply a migration, as needed
/* istanbul ignore next */
export function applyMigrationCtor(_) {
  return async (username, password, locale = 'en-CA', new_password = null) => {
    const data = { username, password, locale };
    if(new_password) {
      data.new_password = new_password;
    }
    try {
      const result = await _.axios.post(`${_.DASHBOARD_API_URL}api/migrate`, data);
      return result;
    } catch(e) {
      return e;
    }
  };
}

// Unlock account
export function unlockAccountCtor(_) {
  return async (username) => {
    const formData = new FormData();
    formData.append('username', username);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/unlock?app_name=advisor`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Reset Password
export function passwordResetCtor(_) {
  return async (username) => {
    try {
      const result = await _.axios.post(`${_.DASHBOARD_API_URL}api/okta-recovery/password?app_name=advisor`, { username });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  }
}

// MFA Challenge
export function mfaChallengeCtor(_) {
  return async (emailToken, factor_id) => {
    const formData = new FormData();
    formData.append('token', encodeURIComponent(emailToken));
    formData.append('factor_id', factor_id);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/mfa-challenge`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Get Enrolled Factors for recovery
export function recoveryFactorsCtor(_) {
  return async (emailToken) => {
    const formData = new FormData();
    formData.append('token', encodeURIComponent(emailToken));
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/factors`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  }
}

// Verify Passcode
export function verifyPasscodeCtor(_) {
  return async (emailToken, factor_id, passcode) => {
    const formData = new FormData();
    formData.append('token', encodeURIComponent(emailToken));
    formData.append('factor_id', factor_id);
    formData.append('passcode', passcode);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/mfa-challenge/verify-passcode`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Answer recovery question for a forgot password or an unlock account transaction
export function checkRecoveryAnswerCtor(_) {
  return async (state_token, answer) => {
    const formData = new FormData();
    formData.append('state_token', encodeURIComponent(state_token));
    formData.append('answer', answer);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/answer`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Ctor to poll for state changes on the Okta Authenticator
export function recoveryPollFactorCtor(_) {
  return async (emailToken, poll_url) => {
    const formData = new FormData();
    formData.append('token', encodeURIComponent(emailToken));
    formData.append('poll_url', poll_url);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/mfa-challenge/verify-push`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

// Reset password for a forgot password transaction. 
export function resetPasswordCtor(_) {
  return async (state_token, new_password) => {
    const formData = new FormData();
    formData.append('state_token', encodeURIComponent(state_token));
    formData.append('new_password', new_password);
    try {
      const result = await _.axios({
        method: 'POST',
        url: `${_.DASHBOARD_API_URL}api/okta-recovery/reset-password`,
        data: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      return result;
    } catch(e) {
      return Promise.reject(e);
    }
  };
}

export const getCiToken = getCiTokenCtor({ axiosInstance: axiosInstance(null) });
export const applyMigration = applyMigrationCtor(_);
export const unlockAccount = unlockAccountCtor(_);
export const passwordReset = passwordResetCtor(_);
export const mfaChallenge = mfaChallengeCtor(_);
export const recoveryFactors = recoveryFactorsCtor(_);
export const verifyPasscode = verifyPasscodeCtor(_);
export const checkRecoveryAnswer = checkRecoveryAnswerCtor(_);
export const recoveryPollFactor = recoveryPollFactorCtor(_);
export const resetPassword = resetPasswordCtor(_);
